import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { 
  ArrowRight,
  Globe,
  Mail,
  ExternalLink
} from "lucide-react";
import BG_1 from "../assets/images/HS_UN_Edited.jpg";
import BG_2 from "../assets/images/HS_UN_Hero_Edited.jpg";
import BG_3 from "../assets/images/Y20_Africa.jpg";
import BG_Mobile_1 from "../assets/images/HHI/HS_UN.jpeg";
import BG_Mobile_2 from "../assets/images/HHI/AGM_Ray.JPG";
import BG_Mobile_3 from "../assets/images/HHI/Y20_Africa.jpg";


// Simple counter hook
const useCountAnimation = (end, duration = 5, startWhen = true) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startWhen) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startWhen]);

  return count;
};

const AnimatedCounter = ({ end, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const count = useCountAnimation(end, 2, isInView);

  return <span ref={ref}>{count}{suffix}</span>;
};

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      image: BG_1,
      mobileImage: BG_Mobile_1,
      title: "Global Youth Leader",
      subtitle: "President of International Cooperative Alliance Youth Committee",
      badge: "ICA YC PRESIDENT"
    },
    {
      image: BG_2,
      mobileImage: BG_Mobile_2,
      title: "Infrastructure Director",
      subtitle: "Leading sustainable development projects",
      badge: "BUSINESS LEADER"
    },
    {
      image: BG_3,
      mobileImage: BG_Mobile_3,
      title: "Social Entrepreneur",
      subtitle: "Creating sustainable ventures for global impact",
      badge: "CHANGEMAKER"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Desktop: Dark background with overlay */}
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-gray-950 via-slate-900 to-gray-900">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentSlide}
            src={heroSlides[currentSlide].image}
            alt="Hero background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Mobile/Tablet: White background */}
      <div className="lg:hidden absolute inset-0 bg-white" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 sm:py-20 lg:py-24">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            
            {/* Left Content - Text */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="w-full space-y-6 order-2 lg:order-1"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-orange-50 to-red-50 lg:from-red-900/80 lg:to-red-800/80 border border-orange-200 lg:border-red-600 rounded-full"
              >
                <span className="text-red-700 lg:text-white text-xs sm:text-sm font-bold tracking-widest">
                  {heroSlides[currentSlide].badge}
                </span>
              </motion.div>

              {/* Name */}
              <div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-3 sm:mb-4">
                  <span className="text-gray-900 lg:text-white">HARSH</span>
                  <br />
                  <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent lg:text-red-500">SANGHANI</span>
                </h1>
                
                <motion.h2
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  className="text-lg sm:text-xl md:text-2xl text-gray-600 lg:text-gray-300 font-light"
                >
                  {heroSlides[currentSlide].title}
                </motion.h2>
              </div>

              {/* Description */}
              <motion.p
                key={`desc-${currentSlide}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-sm sm:text-base lg:text-lg text-gray-600 lg:text-gray-300 leading-relaxed max-w-lg"
              >
                {heroSlides[currentSlide].subtitle}
              </motion.p>

              {/* Stats */}
              <div className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8 pt-4">
                <div>
                  <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent lg:text-white">
                    <AnimatedCounter end={110} suffix="+" />
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 lg:text-gray-400 font-medium">Countries</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent lg:text-white">20</div>
                  <div className="text-xs sm:text-sm text-gray-600 lg:text-gray-400 font-medium">Years Old</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent lg:text-white">
                    <AnimatedCounter end={6} />
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 lg:text-gray-400 font-medium">Ventures</div>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Image (Mobile/Tablet only) */}
            <div className="w-full lg:hidden order-1">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="relative w-full"
              >
                <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl sm:rounded-3xl opacity-20 blur-2xl"></div>
                <div className="relative w-full h-[350px] sm:h-[450px] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-2 sm:border-4 border-white">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentSlide}
                      src={heroSlides[currentSlide].mobileImage}
                      alt="Harsh Sanghani - Global Youth Leader"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.5 }}
                      className="w-full h-full object-cover"
                    />
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Slide indicators */}
          <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2">
            <div className="flex space-x-2 sm:space-x-3">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide 
                      ? 'bg-red-500 w-8' 
                      : 'bg-gray-400 lg:bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;